import { useRef, useEffect, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { CalendarWeek } from '../lib/data-service'

interface CalendarWeekPickerProps {
  weeks: CalendarWeek[]
  selectedRange: { start: string; end: string } | null
  onSelectRange: (range: { start: string; end: string }) => void
  onSelectFiscalWeek?: (fiscalWeek: number) => void
  className?: string
}

export function CalendarWeekPicker({ weeks, selectedRange, onSelectRange, onSelectFiscalWeek, className = '' }: CalendarWeekPickerProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const selectedWeek = weeks.find(w =>
    w.start_date === selectedRange?.start && w.end_date === selectedRange?.end
  )

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        className="flex items-center space-x-2 px-4 py-2 bg-background border border-border rounded-lg hover:bg-muted/50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <span className="text-sm font-medium">
          {selectedWeek ? selectedWeek.display : 'Select Week'}
        </span>
        {selectedWeek && (
          <span className="text-xs text-muted-foreground">
            FW{selectedWeek.fiscal_week}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-80 overflow-y-auto bg-background border border-border rounded-lg shadow-lg z-50">
          {weeks.map(week => (
            <button
              key={week.start_date}
              onClick={() => {
                onSelectRange({ start: week.start_date, end: week.end_date })
                if (onSelectFiscalWeek) {
                  const fw = parseInt(week.fiscal_year.replace('FY', '')) * 100 + week.fiscal_week
                  onSelectFiscalWeek(fw)
                }
                setOpen(false)
              }}
              className={`w-full text-left px-4 py-2.5 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-b-0 ${
                selectedRange?.start === week.start_date && selectedRange?.end === week.end_date
                  ? 'bg-blue-50 dark:bg-blue-900/20'
                  : ''
              }`}
            >
              <div className="font-medium text-sm">{week.display}</div>
              <div className="text-xs text-muted-foreground">
                FW{week.fiscal_week} &middot; {week.quarter} {week.nrf_month} &middot; {week.fiscal_year}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
